import type { Edge, Node } from 'reactflow';

import type {
  ProcessFlow,
  ProcessFlowEdge,
  ProcessFlowNodeType,
} from '../../../api/processes';

export interface FlowCanvasNodeData {
  id: string;
  label: string;
  displayLabel?: string;
  nodeType: ProcessFlowNodeType;
  config: Record<string, unknown>;
  spec?: unknown;
}

export type FlowCanvasNode = Node<FlowCanvasNodeData>;
export type FlowCanvasEdge = Edge & {
  /** ReactFlow uses `type="custom"` to render the add button. Keep the persisted
   * semantic edge type separately so load -> save is lossless. */
  persistedType?: ProcessFlowEdge['type'];
};

export function defaultConfigFor(type: ProcessFlowNodeType): Record<string, unknown> {
  switch (type) {
    case 'start':
      return {};
    case 'approval':
      return {
        resolver: { kind: 'manager_chain', level: 1 },
        sla_hours: 72,
      };
    case 'condition':
      return {
        condition: { field: 'amount', op: 'eq', value: null },
      };
    case 'notification':
      return { channel: 'in_app', message_template: '' };
    case 'cc':
      return { recipients: [] };
    case 'end':
      return {};
  }
}

/** Rehydrate persisted config without merging defaults. Legacy keys are still
 * executable and must not be shadowed by a new resolver/condition key. */
export function configForLoadedNode(
  type: ProcessFlowNodeType,
  config: Record<string, unknown> | null | undefined,
  legacy: boolean,
): Record<string, unknown> {
  const loaded = { ...(config ?? {}) };
  if (
    legacy
    && type === 'approval'
    && loaded.resolver === undefined
    && loaded.approver_role === undefined
  ) {
    return { ...loaded, resolver: { kind: 'manager_chain', level: 1 } };
  }
  return loaded;
}

/** Editor layout constants, shared with the fallback layout below. */
// Must equal FlowEditor's STEP_Y, or a legacy flow with no stored positions lays
// out on a different pitch than a freshly inserted node and the two disagree on
// screen (svdy-rules §10.5). 200 → 128 with it.
const FALLBACK_STEP_Y = 128;
// Must clear the 240px node card (`FlowEditor.tsx`), and match the backend's FLOW_LANE_X so a
// seeded flow and a locally-laid-out one look the same. 280 leaves a 40px gutter for the edge
// elbows to route between lanes.
const FALLBACK_LANE_X = 280;

/**
 * Give every node a position, laying the graph out top-to-bottom when any is missing one.
 *
 * ReactFlow dereferences `node.position.x` while building its internals, so ONE node without a
 * position crashes the whole canvas — which is exactly what happened (owner, 2026-08-23:
 * `Cannot read properties of undefined (reading 'x')` at `createNodeInternals`). The seeded
 * built-in flows in `app/processes/defaults.py` are authored WITHOUT positions, so any tenant
 * that opened one and stepped to Workflow hit it; adding from the catalog just made it the
 * first thing a new admin does.
 *
 * When ANY position is missing the whole graph is laid out rather than only the gaps. That is
 * not laziness: `serializeCanvasFlow` always writes a position for every node, so a flow with
 * a hole was never arranged by a human — mixing authored and guessed coordinates would produce
 * overlaps that look like a rendering bug.
 */
export function withNodePositions(flow: ProcessFlow): ProcessFlow {
  const nodes = flow.nodes ?? [];
  if (nodes.length === 0) return flow;
  if (nodes.every((node) => node.position && typeof node.position.x === 'number')) return flow;

  const edges = flow.edges ?? [];
  const rank = new Map<string, number>();
  const targets = new Set(edges.map((edge) => edge.target));
  const roots = nodes.filter((node) => !targets.has(node.id));
  const queue = (roots.length > 0 ? roots : [nodes[0]]).map((node) => node.id);
  for (const id of queue) rank.set(id, 0);

  // Longest-path ranking, bounded by nodes.length so a cyclic legacy flow cannot spin.
  for (let pass = 0; pass < nodes.length; pass += 1) {
    let changed = false;
    for (const edge of edges) {
      const from = rank.get(edge.source);
      if (from === undefined) continue;
      const next = from + 1;
      if ((rank.get(edge.target) ?? -1) < next) {
        rank.set(edge.target, next);
        changed = true;
      }
    }
    if (!changed) break;
  }

  // Count lanes per row FIRST so a row can be centred as a whole. The comment below used to
  // claim these were "centred on the trunk"; the arithmetic did not do that — see the
  // backend's `_positioned_flow` for the measurement (41 of 41 catalogue definitions
  // overlapped by 110px against a 240px card).
  const laneCount = new Map<number, number>();
  nodes.forEach((node) => {
    const row = rank.get(node.id) ?? 0;
    laneCount.set(row, (laneCount.get(row) ?? 0) + 1);
  });

  const usedLanes = new Map<number, number>();
  return {
    ...flow,
    nodes: nodes.map((node) => {
      const row = rank.get(node.id) ?? 0;
      const lane = usedLanes.get(row) ?? 0;
      usedLanes.set(row, lane + 1);
      // Centring a row of k lanes shifts EVERY lane by -(k-1)/2 lanes. Shifting all but the
      // first by half a lane instead pulls lane 1 back onto lane 0, which is the defect.
      const offset = ((laneCount.get(row) ?? 1) - 1) / 2;
      return {
        ...node,
        position: { x: Math.round((lane - offset) * FALLBACK_LANE_X), y: row * FALLBACK_STEP_Y },
      };
    }),
  };
}

/**
 * Guarantee the two terminals stay wired: a `start` always has a way out and an `end` always has
 * a way in.
 *
 * Product rule the owner stated directly (2026-08-23): "最后的开始和结束节点是从始至终要存在，
 * 而且保持连接，如果中间新增的所有节点被删除". Deleting a node only bridged the gap when it had
 * exactly one edge in and one out, so removing a branching step (an approval has two) left the
 * start dangling and the flow silently unpublishable.
 *
 * Deliberately conservative: it only ADDS the edges needed to reach that invariant, and it uses
 * the DEFAULT (unnamed) handle so it can never fabricate a branch outcome — inventing an
 * `approved` edge would change what the engine executes.
 */
export function ensureTerminalConnectivity(
  nodes: ProcessFlow['nodes'],
  edges: ProcessFlowEdge[],
): ProcessFlowEdge[] {
  const starts = nodes.filter((node) => node.type === 'start');
  const ends = nodes.filter((node) => node.type === 'end');
  if (starts.length === 0 || ends.length === 0) return edges;

  const result = [...edges];
  const hasOutgoing = (id: string) => result.some((edge) => edge.source === id);
  const hasIncoming = (id: string) => result.some((edge) => edge.target === id);
  const connect = (source: string, target: string) => {
    if (result.some((edge) => edge.source === source && edge.target === target)) return;
    result.push({
      id: `edge-${source}-${target}`,
      source,
      target,
      sourceHandle: null,
      targetHandle: null,
      type: 'smoothstep',
    });
  };

  for (const start of starts) {
    if (hasOutgoing(start.id)) continue;
    // Prefer a real orphaned middle step over jumping straight to the end, so a delete that
    // leaves other steps behind reconnects the chain instead of stranding them.
    const orphan = nodes.find((node) =>
      node.id !== start.id
      && node.type !== 'start'
      && node.type !== 'end'
      && !hasIncoming(node.id));
    connect(start.id, (orphan ?? ends[0]).id);
  }

  for (const end of ends) {
    if (hasIncoming(end.id)) continue;
    const dangling = nodes.find((node) =>
      node.id !== end.id
      && node.type !== 'end'
      && !hasOutgoing(node.id));
    connect((dangling ?? starts[0]).id, end.id);
  }

  return result;
}

export function branchOutcomes(type: ProcessFlowNodeType): string[] {
  if (type === 'approval') return ['approved', 'rejected'];
  if (type === 'condition') return ['true', 'false'];
  return [];
}

/** The only canvas -> API serializer. Every persisted field is listed here on
 * purpose; ReactFlow selection/measurement state never enters the JSONB flow. */
export function serializeCanvasFlow(
  nodes: FlowCanvasNode[],
  edges: FlowCanvasEdge[],
): ProcessFlow {
  return {
    contract_version: 1,
    nodes: nodes.map((node) => ({
      id: node.id,
      type: node.data.nodeType,
      position: { x: node.position.x, y: node.position.y },
      data: {
        label: node.data.label,
        config: node.data.config,
      },
    })),
    edges: edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle ?? null,
      targetHandle: edge.targetHandle ?? null,
      type: edge.persistedType ?? 'smoothstep',
      label: typeof edge.label === 'string' ? edge.label : null,
      data: edge.data ? { ...edge.data } : undefined,
    })),
  };
}



function hasNode(nodes: ProcessFlow['nodes'], id: string): boolean {
  return nodes.some((node) => node.id === id);
}

function findEdge(
  edges: ProcessFlowEdge[],
  source: string,
  target?: string,
): ProcessFlowEdge | undefined {
  return edges.find((edge) =>
    edge.source === source && (target === undefined || edge.target === target),
  );
}

/** Collapse the exact branch/merge scaffolding emitted by the pre-v1 Designer.
 * Those visual nodes were persisted as fake `condition` engine nodes. The only
 * executable meaning recoverable from that shape is one real condition whose true
 * and false handles both continue to the old merge target. */
export function normalizeLegacySyntheticFlow(flow: ProcessFlow): ProcessFlow {
  let nodes = flow.nodes.slice();
  let edges = flow.edges.slice();

  const roots = nodes.filter(
    (node) => node.type === 'condition'
      && !/-branch[12]$/.test(node.id)
      && !/-merge$/.test(node.id),
  );
  for (const root of roots) {
    const branchIds = [`${root.id}-branch1`, `${root.id}-branch2`];
    const mergeId = `${root.id}-merge`;
    if (!hasNode(nodes, branchIds[0]) || !hasNode(nodes, branchIds[1])) continue;
    if (!hasNode(nodes, mergeId)) continue;

    const branchEdges = [
      findEdge(edges, root.id, branchIds[0]),
      findEdge(edges, root.id, branchIds[1]),
    ];
    const mergeExit = findEdge(edges, mergeId);
    if (branchEdges.some((edge) => !edge) || !mergeExit) continue;

    const synthetic = new Set([...branchIds, mergeId]);
    nodes = nodes.filter((node) => !synthetic.has(node.id));
    edges = edges.filter((edge) =>
      edge.source !== root.id
      && !synthetic.has(edge.source)
      && !synthetic.has(edge.target),
    );
    edges.push(
      {
        id: `edge-${root.id}-true-${mergeExit.target}`,
        source: root.id,
        target: mergeExit.target,
        sourceHandle: 'true',
        targetHandle: mergeExit.targetHandle,
        type: mergeExit.type,
        label: branchEdges[0]?.label ?? null,
      },
      {
        id: `edge-${root.id}-false-${mergeExit.target}`,
        source: root.id,
        target: mergeExit.target,
        sourceHandle: 'false',
        targetHandle: mergeExit.targetHandle,
        type: mergeExit.type,
        label: branchEdges[1]?.label ?? null,
      },
    );
  }

  if (flow.contract_version !== 1) {
    const typeById = new Map(nodes.map((node) => [node.id, node.type]));
    edges = edges.map((edge) => {
      if (typeById.get(edge.source) !== 'approval') return edge;
      const siblings = edges.filter((candidate) => candidate.source === edge.source);
      const dataOn = edge.data?.on;
      if (
        siblings.length === 1
        && !edge.sourceHandle
        && !dataOn
      ) {
        return { ...edge, sourceHandle: 'approved' };
      }
      return edge;
    });
  }

  return { ...flow, nodes, edges };
}


export type CanvasValidationIssue =
  | 'missing_nodes'
  | 'unknown_edge_endpoint'
  | 'unreachable_node'
  | 'cycle'
  | 'outgoing_edge_count'
  | 'branch_outcome'
  | 'approval_config'
  | 'condition_config'
  | 'cc_recipients';

/** Early author feedback. Publish/Start remain authoritative in the backend; this
 * mirrors the user-fixable v1 checks so Validate cannot claim success immediately
 * before Publish rejects the same canvas. */
/**
 * One walk, two views: the issue KINDS and the node ids that carry them.
 *
 * The ids used to be discarded, so the canvas could only say "Fix the highlighted
 * setup" while highlighting nothing (owner screenshot 2026-08-23 19:48). Producing
 * both from the same pass means the sentence and the highlight cannot disagree —
 * two separate walks would (svdy-rules §10.5).
 *
 * Graph-level issues (missing start/end, a cycle, an unreachable node) carry no
 * single id and are deliberately absent from `nodeIds`: highlighting an arbitrary
 * node for them would point at the wrong thing.
 */
export function inspectCanvasFlow(
  nodes: FlowCanvasNode[],
  edges: FlowCanvasEdge[],
): { issues: CanvasValidationIssue[]; nodeIds: string[] } {
  const issues = new Set<CanvasValidationIssue>();
  const offending = new Set<string>();
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const starts = nodes.filter((node) => node.data.nodeType === 'start');
  const ends = nodes.filter((node) => node.data.nodeType === 'end');
  if (starts.length !== 1 || ends.length === 0) issues.add('missing_nodes');

  const outgoing = new Map<string, FlowCanvasEdge[]>();
  for (const edge of edges) {
    if (!byId.has(edge.source) || !byId.has(edge.target)) {
      issues.add('unknown_edge_endpoint');
      continue;
    }
    outgoing.set(edge.source, [...(outgoing.get(edge.source) ?? []), edge]);
  }

  for (const node of nodes) {
    const type = node.data.nodeType;
    const nodeEdges = outgoing.get(node.id) ?? [];
    const config = node.data.config ?? {};
    if (type === 'end') {
      if (nodeEdges.length) { issues.add('outgoing_edge_count'); offending.add(node.id); }
      continue;
    }
    if (type === 'approval' || type === 'condition') {
      const outcomes = nodeEdges
        .map((edge) => edge.sourceHandle ?? edge.data?.on)
        .filter((outcome): outcome is string => typeof outcome === 'string' && outcome.length > 0);
      const required = branchOutcomes(type);
      if (
        outcomes.length !== nodeEdges.length
        || required.some((outcome) => !outcomes.includes(outcome))
        || new Set(outcomes).size !== outcomes.length
      ) {
        issues.add('branch_outcome');
        offending.add(node.id);
      }
    } else if (nodeEdges.length !== 1) {
      issues.add('outgoing_edge_count');
      offending.add(node.id);
    }

    if (type === 'approval') {
      const resolver = config.resolver as Record<string, unknown> | undefined;
      const legacy = config.approver_role;
      const resolverValid = resolver?.kind === 'manager_chain'
        ? Number(resolver.level ?? 1) >= 1
        : resolver?.kind === 'specific_worker'
          ? typeof resolver.worker_id === 'string' && resolver.worker_id.length > 0
          : resolver?.kind === 'role'
            ? typeof resolver.role_code === 'string' && resolver.role_code.length > 0
            : typeof legacy === 'string' && legacy.length > 0;
      if (!resolverValid) { issues.add('approval_config'); offending.add(node.id); }
    } else if (type === 'condition') {
      const condition = config.condition as Record<string, unknown> | undefined;
      const structured = condition
        && typeof condition.field === 'string'
        && condition.field.length > 0
        && typeof condition.op === 'string'
        && condition.value !== null
        && condition.value !== ''
        && condition.value !== undefined;
      const legacy = typeof config.expression === 'string'
        && /^data\.\w+\s*(?:===?|!==?|>=?|<=?)\s*.+$/.test(config.expression.trim());
      if (!structured && !legacy) { issues.add('condition_config'); offending.add(node.id); }
    } else if (type === 'cc') {
      const recipients = config.recipients;
      if (!Array.isArray(recipients) || recipients.length === 0) {
        issues.add('cc_recipients');
        offending.add(node.id);
      }
    }
  }

  if (starts.length === 1) {
    const reachable = new Set<string>();
    const queue = [starts[0].id];
    while (queue.length) {
      const current = queue.shift()!;
      if (reachable.has(current)) continue;
      reachable.add(current);
      for (const edge of outgoing.get(current) ?? []) queue.push(edge.target);
    }
    if (nodes.some((node) => !reachable.has(node.id))) issues.add('unreachable_node');
  }

  const colour = new Map(nodes.map((node) => [node.id, 0]));
  const visit = (id: string) => {
    colour.set(id, 1);
    for (const edge of outgoing.get(id) ?? []) {
      if (colour.get(edge.target) === 1) issues.add('cycle');
      else if (colour.get(edge.target) === 0) visit(edge.target);
    }
    colour.set(id, 2);
  };
  for (const node of nodes) if (colour.get(node.id) === 0) visit(node.id);
  return { issues: Array.from(issues), nodeIds: Array.from(offending) };
}

/**
 * The issue kinds only. Kept because six existing assertions read this shape; it
 * delegates rather than re-walking.
 */
export function validateCanvasFlow(
  nodes: FlowCanvasNode[],
  edges: FlowCanvasEdge[],
): CanvasValidationIssue[] {
  return inspectCanvasFlow(nodes, edges).issues;
}

/**
 * A VIEW over the flow with some branch owners' subtrees folded away.
 *
 * Why this exists: plan A's pitch change (STEP_Y 200 → 128) buys one thing, a 4-row
 * graph at zoom 0.77 instead of 0.53. It cannot buy a fifth row — below ~100 the
 * cards touch, so depth is a ceiling that pitch cannot raise. Collapsing removes
 * ROWS instead of shrinking them, which is what Kissflow's vertical builder does
 * and why it survives deep flows.
 *
 * The rule for "belongs to this branch" is DOMINANCE, not reachability: a node is
 * hidden only if every path to it from `start` passes through the collapsed owner.
 * Plain reachability would hide a step that also hangs off something outside the
 * branch, removing a row the admin can still reach by another route.
 *
 * Pure, and it never touches its inputs — the persisted flow is serialized from
 * those same arrays, so mutating them would make folding a branch dirty the form
 * and save a different graph than the one on screen.
 */
export function collapseBranches(
  nodes: FlowCanvasNode[],
  edges: FlowCanvasEdge[],
  collapsed: ReadonlySet<string>,
): {
  nodes: FlowCanvasNode[];
  edges: FlowCanvasEdge[];
  /** Owner id -> how many steps it is currently hiding. Drives the node's chip. */
  hiddenByNode: Map<string, number>;
} {
  const hiddenByNode = new Map<string, number>();
  if (collapsed.size === 0) return { nodes, edges, hiddenByNode };

  const byId = new Map(nodes.map((node) => [node.id, node]));
  const outgoing = new Map<string, FlowCanvasEdge[]>();
  for (const edge of edges) {
    outgoing.set(edge.source, [...(outgoing.get(edge.source) ?? []), edge]);
  }
  const startId = nodes.find((node) => node.data.nodeType === 'start')?.id;

  /** Reachable from `start` without ever entering `blocked`. */
  const reachableAvoiding = (blocked: string): Set<string> => {
    const seen = new Set<string>();
    if (!startId || startId === blocked) return seen;
    const queue = [startId];
    while (queue.length) {
      const current = queue.shift()!;
      if (seen.has(current) || current === blocked) continue;
      seen.add(current);
      for (const edge of outgoing.get(current) ?? []) queue.push(edge.target);
    }
    return seen;
  };

  const hidden = new Set<string>();
  const rejoinFor = new Map<string, string[]>();

  for (const ownerId of Array.from(collapsed)) {
    const owner = byId.get(ownerId);
    // Terminals own no branch, and the graph must always keep a way in and out.
    if (!owner || owner.data.nodeType === 'start' || owner.data.nodeType === 'end') continue;
    const branchEdges = outgoing.get(ownerId) ?? [];
    // One outgoing edge is a linear step: there is no branch to fold, and a
    // chevron on it would do nothing.
    if (branchEdges.length < 2) continue;

    const withoutOwner = reachableAvoiding(ownerId);
    const owned = new Set<string>();
    const rejoins = new Set<string>();
    const queue = branchEdges.map((edge) => edge.target);
    while (queue.length) {
      const current = queue.shift()!;
      if (owned.has(current) || current === ownerId) continue;
      const node = byId.get(current);
      if (!node) continue;
      // Dominated by the owner, and not a terminal: this row can fold away.
      const dominated = !withoutOwner.has(current)
        && node.data.nodeType !== 'start'
        && node.data.nodeType !== 'end';
      if (!dominated) {
        rejoins.add(current);
        continue;
      }
      owned.add(current);
      for (const edge of outgoing.get(current) ?? []) queue.push(edge.target);
    }
    if (owned.size === 0) continue;
    owned.forEach((id) => hidden.add(id));
    hiddenByNode.set(ownerId, owned.size);
    rejoinFor.set(ownerId, Array.from(rejoins));
  }

  if (hidden.size === 0) return { nodes, edges, hiddenByNode };

  const visibleNodes = nodes.filter((node) => !hidden.has(node.id));
  const visible = new Set(visibleNodes.map((node) => node.id));
  const visibleEdges = edges.filter(
    (edge) => visible.has(edge.source) && visible.has(edge.target),
  );

  // One synthetic edge per owner→rejoin pair. Two to the same target would draw a
  // doubled line; none would leave `end` unreachable, which is the exact shape
  // `ensureTerminalConnectivity` exists to prevent.
  for (const [ownerId, rejoins] of Array.from(rejoinFor.entries())) {
    for (const target of Array.from(new Set(rejoins))) {
      if (!visible.has(target)) continue;
      if (visibleEdges.some((edge) => edge.source === ownerId && edge.target === target)) continue;
      visibleEdges.push({
        id: `collapsed-${ownerId}-${target}`,
        source: ownerId,
        target,
        type: 'custom',
        data: { collapsed: true },
      } as unknown as FlowCanvasEdge);
    }
  }

  return { nodes: visibleNodes, edges: visibleEdges, hiddenByNode };
}

/**
 * Guarantee every branch outcome has a destination. P0, owner 2026-08-23.
 *
 * The reported symptom: connect approval →(approved) condition, delete the
 * condition, and the approval visibly loses a line. `handleDeleteNode` bridged only
 * a 1-in/1-out step, with a comment arguing that a branching step "cannot" be
 * bridged because it would have to pick one of ITS outcomes. True, and beside the
 * point — the cost of not choosing was landed on the PREDECESSOR, whose `approved`
 * outcome ended up wired to nothing. An approval with one of two outcomes unwired
 * is an invalid flow that also looks broken.
 *
 * This is the backstop half of the fix (session-discipline §12). It runs after node
 * deletion AND after any edge change, because the second surface — ReactFlow lets
 * the user select the "approved" line and press Delete, and `handleEdgesChange`
 * forwards that verbatim — has no deleted node to re-point from.
 *
 * Target choice, in order:
 *   1. a node this one already reaches (so a delete lands where the flow continues
 *      rather than silently shortening the process to End);
 *   2. `end`, which is what "approved, and nothing after it" means.
 *
 * Returns the SAME array when nothing is wrong, so a healthy flow does not churn
 * React state on every render.
 */
export function repairBranchOutcomes(
  nodes: FlowCanvasNode[],
  edges: FlowCanvasEdge[],
): FlowCanvasEdge[] {
  const endId = nodes.find((node) => node.data.nodeType === 'end')?.id;
  const additions: FlowCanvasEdge[] = [];

  for (const node of nodes) {
    const required = branchOutcomes(node.data.nodeType);
    if (required.length === 0) continue;
    const own = edges.filter((edge) => edge.source === node.id);
    for (const outcome of required) {
      if (own.some((edge) => edge.sourceHandle === outcome)) continue;
      // Prefer somewhere this node already goes; a handle-less edge left over from
      // a delete is exactly that.
      const target = own.find((edge) => edge.target !== node.id)?.target ?? endId;
      if (!target || target === node.id) continue;
      additions.push({
        id: `repair-${node.id}-${outcome}`,
        source: node.id,
        target,
        sourceHandle: outcome,
        type: 'custom',
        persistedType: 'smoothstep',
      } as unknown as FlowCanvasEdge);
    }
  }

  if (additions.length === 0) return edges;
  return edges.concat(additions);
}
